import { getCookie } from 'cookies-next';
import mixpanel from 'mixpanel-browser';

declare module 'mixpanel-browser' {
  interface OverridedMixpanel {
    start_session_recording(): void;
    stop_session_recording(): void;
  }
}

export class Mixpanel {
  constructor(id: string) {
    const isSuperUser = Boolean(getCookie('access_super_token'));
    const isLocalHost = Boolean(process.env.APP_ENV === 'local');

    this.init(id);

    if (isSuperUser || isLocalHost) {
      mixpanel.disable();
    }
    this.isSuperUser = isSuperUser;
  }

  /** mix panel init */
  init(id: string) {
    mixpanel.init(id, {
      debug: Boolean(process.env.APP_ENV !== 'prod'),
      // Session Replay 설정
      record_sessions_percent: 0, // 기본적으로 비활성화, startSessionRecording()으로 수동 제어
      record_idle_timeout_ms: 1800000, // 30분 (비활동 시 녹화 종료)
    });
  }

  /** super props register하는 메소드 */
  register(options: object) {
    mixpanel.register_once({
      ...options,
    });
  }

  /** user identify */
  identify(userId: string) {
    mixpanel.identify(userId);
  }

  get_property(property: string) {
    return mixpanel.get_property(property);
  }

  /** event track */
  track(eventName: string, properties?: any) {
    if (!mixpanel.get_distinct_id()) {
      return;
    }

    mixpanel.track(eventName, {
      ...this.commonProperties,
      ...properties,
    });
  }

  /** user set */
  setPeople(options: object) {
    mixpanel.people.set({
      ...options,
    });
  }

  /** user set once (기존 값이 있으면 덮어씌우지 않음) */
  setPeopleOnce(options: object) {
    mixpanel.people.set_once({
      ...options,
    });
  }

  /** 정보 reset */
  reset() {
    mixpanel.reset();
  }

  setCommonProperties(properties: object) {
    this.commonProperties = properties;
  }

  /** session recording 시작 */
  startSessionRecording() {
    if (this.isSuperUser) return;
    mixpanel.start_session_recording();
  }

  /** session recording 중지 */
  stopSessionRecording() {
    mixpanel.stop_session_recording();
  }

  private isSuperUser: boolean;
  private commonProperties: object = {};
}

let mixpanelInstance: Mixpanel | null = null;

export function initMixpanelInstance(id: string) {
  if (typeof window === 'undefined') return; // SSR 환경에서는 초기화하지 않음
  if (!mixpanelInstance) {
    mixpanelInstance = new Mixpanel(id);
  }
}

export function getMixpanelInstance(): Mixpanel | null {
  return mixpanelInstance;
}

export function mixpanelRegister(options: object) {
  const instance = getMixpanelInstance();
  if (!instance) return;
  return instance.register(options);
}

export function mixpanelIdentify(userId: string) {
  const instance = getMixpanelInstance();
  if (!instance) return;
  return instance.identify(userId);
}

export function mixpanelGetProperty(property: string) {
  const instance = getMixpanelInstance();
  if (!instance) return;
  return instance.get_property(property);
}

export function mixpanelTrack(eventName: string, properties?: any) {
  const instance = getMixpanelInstance();
  if (!instance) return;
  return instance.track(eventName, properties);
}

export function mixpanelSetPeople(options: object) {
  const instance = getMixpanelInstance();
  if (!instance) return;
  return instance.setPeople(options);
}

export function mixpanelSetPeopleOnce(options: object) {
  const instance = getMixpanelInstance();
  if (!instance) return;
  return instance.setPeopleOnce(options);
}

export function mixpanelReset() {
  const instance = getMixpanelInstance();
  if (!instance) return;
  return instance.reset();
}

export function mixpanelSetCommonProperties(properties: object) {
  const instance = getMixpanelInstance();
  if (!instance) return;
  return instance.setCommonProperties(properties);
}

export function mixpanelStartSessionRecording() {
  const instance = getMixpanelInstance();
  if (!instance) return;
  return instance.startSessionRecording();
}

export function mixpanelStopSessionRecording() {
  const instance = getMixpanelInstance();
  if (!instance) return;
  return instance.stopSessionRecording();
}
